// El mensaje que se le enseña al operador cuando una orden MDM falla: siempre en español.
import { ApiError } from '@/api/errors';

export const MDM_ACTION_GENERIC_ERROR =
    'No se pudo completar la orden. Intenta de nuevo; si sigue pasando, repórtalo.';

export const MDM_ACTION_VALIDATION_ERROR =
    'Hay datos inválidos en la solicitud: revisa el motivo y la nota.';

const PROVIDER_UNRESPONSIVE = 'El proveedor del MDM no respondió. Intenta de nuevo.';

const MESSAGE_BY_CODE: Readonly<Record<string, string>> = {
    MDM_PROVIDER_ERROR: PROVIDER_UNRESPONSIVE,
    MDM_CONNECTOR_TIMEOUT: PROVIDER_UNRESPONSIVE,
    MDM_CONNECTOR_UNAVAILABLE: PROVIDER_UNRESPONSIVE,
    MDM_DEVICE_NOT_FOUND: 'Este crédito no tiene un equipo registrado en el MDM.',
    PERMISSION_DENIED: 'No tienes permiso para mandar esta orden.',
    SESSION_EXPIRED: 'Tu sesión expiró. Inicia sesión de nuevo.',
};

/** El rechazo de un ítem sin traducción: nunca se enseña el texto crudo del proveedor. */
export const MDM_PROVIDER_REJECTED = 'El proveedor rechazó la orden.';

/** Códigos del proveedor que viajan dentro del 200 con `status: ERROR`. */
const ITEM_MESSAGE_BY_CODE: Readonly<Record<string, string>> = {
    PROVIDER_ERROR: PROVIDER_UNRESPONSIVE,
    PROVIDER_TIMEOUT: PROVIDER_UNRESPONSIVE,
    PROVIDER_RATE_LIMITED: PROVIDER_UNRESPONSIVE,
    PROVIDER_TRANSIENT_ERROR: PROVIDER_UNRESPONSIVE,
    PROVIDER_UNREACHABLE: PROVIDER_UNRESPONSIVE,
    DEVICE_NOT_FOUND: MESSAGE_BY_CODE.MDM_DEVICE_NOT_FOUND,
    PROVIDER_CLIENT_ERROR: MDM_PROVIDER_REJECTED,
    DEVICE_NOT_ENROLLED:
        'El equipo no está enrolado en el MDM. Revisa el enrolamiento en Control MDM.',
    UNLOCK_UNTIL_REQUIRED: 'Este equipo requiere indicar hasta cuándo queda desbloqueado.',
    INVALID_REQUEST: MDM_ACTION_VALIDATION_ERROR,
    PROVIDER_NOT_SUPPORTED: 'El proveedor MDM de este equipo no admite esta orden.',
    DEVICE_CAPABILITY_NOT_SUPPORTED: 'Este equipo no admite esta orden por cómo se enroló.',
    DEVICE_STATE_NOT_SUPPORTED:
        'El equipo no admite esta orden en el estado en que está ahora. Revisa su estado en Control MDM.',
    CONNECTOR_NOT_WIRED:
        'La conexión con el proveedor MDM de este equipo no está configurada. Repórtalo a soporte.',
    AUTH_FAILED:
        'El proveedor MDM rechazó las credenciales de Fiado. Repórtalo a soporte; reintentar no lo resuelve.',
    FORBIDDEN: 'El proveedor MDM no permite esta orden para este equipo. Repórtalo a soporte.',
};

/** Señales mínimas de que el msg del back ya viene en español y se puede enseñar tal cual. */
const SPANISH_HINT = /[áéíóúñ¿¡]|\b(el|la|los|las|del|de|una?|es|está|equipo|crédito)\b/i;

function isValidationError(error: ApiError): boolean {
    return (
        error.httpStatus === 400 ||
        error.code === 'VALIDATION_ERROR' ||
        error.code.startsWith('INVALID_')
    );
}

/** Código conocido → su texto; validación y 5xx → texto fijo; otro 4xx → su msg si está en español. */
export function mdmActionErrorMessage(error: unknown): string {
    if (!(error instanceof ApiError)) {
        return error instanceof Error && error.name === 'RequestTimeoutError'
            ? error.message
            : MDM_ACTION_GENERIC_ERROR;
    }
    if (Object.hasOwn(MESSAGE_BY_CODE, error.code)) return MESSAGE_BY_CODE[error.code];
    if (error.httpStatus === 502 || error.httpStatus === 504) return PROVIDER_UNRESPONSIVE;
    if (error.httpStatus >= 500) return MDM_ACTION_GENERIC_ERROR;
    if (isValidationError(error)) return MDM_ACTION_VALIDATION_ERROR;
    // Sin msg del back, `ApiError` cae al propio código: eso nunca se enseña.
    const hasOwnMessage = error.message !== error.code && SPANISH_HINT.test(error.message);
    return hasOwnMessage ? error.message : MDM_ACTION_GENERIC_ERROR;
}

/** Código conocido → su texto; msg ya en español → tal cual; lo demás → «El proveedor rechazó la orden.». */
export function mdmItemErrorMessage(
    error: { code?: string; message?: string } | undefined,
): string {
    const code = error?.code ?? '';
    if (Object.hasOwn(ITEM_MESSAGE_BY_CODE, code)) return ITEM_MESSAGE_BY_CODE[code];
    const message = error?.message ?? '';
    return SPANISH_HINT.test(message) ? message : MDM_PROVIDER_REJECTED;
}
